/** From the teacher's photo to the decoder's pixels, on the phone (STORY-089 M3).
 *
 * The page never sends or keeps the photo: it is decoded here, read in a worker, drawn on the
 * sheet, and every copy (the bitmap, the canvases) is dropped when the sheet is done with it. No
 * object URL either: the sheet draws the bitmap to a canvas. */
import type { CardScan, Pixels } from "./decode";

/** The long side the decoder reads at most. A 125 mm card at the back of a room needs a
 * camera's full width, and iOS caps a canvas's area (inferred, not measured). */
export const MAX_SIDE = 3200;

/** A size no longer than `max` on its long side, keeping the aspect; never enlarged. */
export function fitWithin(width: number, height: number, max = MAX_SIDE): { width: number; height: number; scale: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)), scale };
}

/** A JPEG's EXIF orientation (1 to 8; 1 when it carries none) and its stored frame size, or
 * null for a file that is not a JPEG or whose header cannot be read. Reads the segments up to the
 * start of the scan: the APP1 `Exif` segment's IFD0 and the start-of-frame header. */
export function jpegInfo(bytes: Uint8Array): { orientation: number; width: number; height: number } | null {
  const at = (i: number) => bytes[i] ?? -1;
  if (at(0) !== 0xff || at(1) !== 0xd8) return null;
  let orientation = 1;
  let i = 2;
  while (i + 4 <= bytes.length) {
    if (at(i) !== 0xff) return null;
    const marker = at(i + 1);
    if (marker === 0xda || marker === 0xd9) return null; // the scan began before any frame header
    const length = (at(i + 2) << 8) | at(i + 3);
    if (length < 2) return null;
    // SOF0 to SOF15, apart from DHT (C4), JPG (C8) and DAC (CC): the frame's height and width
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      const height = (at(i + 5) << 8) | at(i + 6);
      const width = (at(i + 7) << 8) | at(i + 8);
      return height > 0 && width > 0 ? { orientation, width, height } : null;
    }
    if (marker === 0xe1 && at(i + 4) === 0x45 && at(i + 5) === 0x78 && at(i + 6) === 0x69 && at(i + 7) === 0x66 && at(i + 8) === 0 && at(i + 9) === 0) {
      const tiff = i + 10;
      const little = at(tiff) === 0x49 && at(tiff + 1) === 0x49;
      if (!little && !(at(tiff) === 0x4d && at(tiff + 1) === 0x4d)) return null;
      const u16 = (o: number) => (little ? at(o) | (at(o + 1) << 8) : (at(o) << 8) | at(o + 1));
      const u32 = (o: number) => (little ? u16(o) + u16(o + 2) * 65536 : u16(o) * 65536 + u16(o + 2));
      const ifd = tiff + u32(tiff + 4);
      const entries = u16(ifd);
      if (entries < 0 || ifd + 2 + entries * 12 > bytes.length) return null;
      for (let e = 0; e < entries; e++) {
        const entry = ifd + 2 + e * 12;
        if (u16(entry) !== 0x0112) continue;
        const value = u16(entry + 8);
        if (value < 1 || value > 8) return null;
        orientation = value;
      }
    }
    i += 2 + length;
  }
  return null;
}

type CreateBitmap = (blob: Blob, options?: ImageBitmapOptions) => Promise<ImageBitmap>;

/** Whether a bitmap decoded from a file with this header is upright. `applied` says the engine
 * took `imageOrientation: "from-image"`.
 *
 * - A quarter turn (orientation 5 to 8, a phone held upright) swaps the sides, so an upright
 *   bitmap is as wide as the stored frame is high. That holds or fails on either path, so it
 *   catches an engine that ignores the option without a TypeError too.
 * - No turn (orientation 1) is upright on either path.
 * - A half turn or a mirror (2 to 4) cannot be told from the size: upright only if the engine
 *   took the option.
 * - Not a JPEG, or a header that cannot be read: upright only if the engine took the option. */
export function uprightFor(info: { orientation: number; width: number; height: number } | null, bitmap: { width: number; height: number }, applied: boolean): boolean {
  if (!info) return applied;
  if (info.orientation >= 5) return bitmap.width === info.height && bitmap.height === info.width;
  if (info.orientation === 1) return true;
  return applied;
}

/** The photo, decoded, and whether it is upright (`uprightFor`). `imageOrientation:
 * "from-image"` applies its EXIF orientation (the HTML specification's rule). An engine that
 * rejects the option with a TypeError decodes it again without, and may or may not have turned
 * it. A bitmap that is not upright is not posted from: every card would read a quarter turn
 * off. */
export async function bitmapOf(blob: Blob, create: CreateBitmap = (b, o) => createImageBitmap(b, o as ImageBitmapOptions)): Promise<{ bitmap: ImageBitmap; upright: boolean }> {
  let bitmap: ImageBitmap | null = null;
  let applied = true;
  try {
    bitmap = await create(blob, { imageOrientation: "from-image" });
  } catch (e) {
    if (!(e instanceof TypeError)) throw e;
  }
  if (!bitmap) {
    applied = false;
    bitmap = await create(blob);
  }
  let info: ReturnType<typeof jpegInfo> = null;
  try {
    // the APP1 segment and the frame header sit at the start of the file; 256 KiB covers them
    // past a large embedded thumbnail
    info = jpegInfo(new Uint8Array(await blob.slice(0, 262_144).arrayBuffer()));
  } catch {
    info = null;
  }
  return { bitmap, upright: uprightFor(info, bitmap, applied) };
}

/** Draw a bitmap to a canvas of at most MAX_SIDE and read its pixels back; the canvas is
 * emptied before returning, so only the returned array holds them. */
export function pixelsOf(bitmap: ImageBitmap): Pixels & { data: Uint8ClampedArray; scale: number } {
  const size = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("no 2d canvas");
  ctx.drawImage(bitmap, 0, 0, size.width, size.height);
  const image = ctx.getImageData(0, 0, size.width, size.height);
  canvas.width = 0;
  canvas.height = 0;
  return { width: image.width, height: image.height, data: image.data, scale: size.scale };
}

type Reply = { ready: true } | { ok: true; scan: CardScan } | { ok: false };

/** The most a photo's decode may take before the sheet says it could not be read. */
export const DECODE_TIMEOUT_MS = 15_000;

/** What `decodeOffThread` needs of a worker; tests pass a fake. */
export type WorkerLike = {
  onmessage: ((e: MessageEvent<Reply>) => void) | null;
  onerror: ((e: unknown) => void) | null;
  onmessageerror: ((e: unknown) => void) | null;
  postMessage(message: unknown, transfer: Transferable[]): void;
  terminate(): void;
};

const startWorker = (): WorkerLike =>
  new Worker(new URL("./decode.worker.ts", import.meta.url), { type: "module" }) as unknown as WorkerLike;

const onThisThread = async (pixels: Pixels): Promise<CardScan> => (await import("./decode")).decodeCards(pixels);

/** Read the cards off the main thread, in `decode.worker.ts`.
 *
 * - The pixel buffer is handed over (not copied) only once the worker says it is ready, so a
 *   worker that fails to load leaves the buffer here, and the photo is read on this thread
 *   instead. The same happens where a worker cannot be constructed at all.
 * - A worker that never answers, or answers with a message that cannot be read
 *   (`messageerror`), fails the photo after at most `timeoutMs`; so does `signal` (the sheet
 *   closing, or a newer photo). The worker is terminated either way. */
export async function decodeOffThread(
  pixels: Pixels & { data: Uint8ClampedArray },
  signal?: AbortSignal,
  opts: { start?: () => WorkerLike; timeoutMs?: number } = {},
): Promise<CardScan> {
  if (signal?.aborted) throw new Error("the photo is no longer wanted");
  let worker: WorkerLike | null = null;
  try {
    worker = (opts.start ?? startWorker)();
  } catch {
    worker = null;
  }
  if (!worker) return onThisThread(pixels);
  const w = worker;
  try {
    return await new Promise<CardScan>((resolve, reject) => {
      let sent = false;
      const done = (fn: () => void) => {
        clearTimeout(timer);
        signal?.removeEventListener("abort", abort);
        w.onmessage = null;
        w.onerror = null;
        w.onmessageerror = null;
        fn();
      };
      const fail = () => done(() => reject(new Error("the photo could not be read")));
      const abort = () => fail();
      const timer = setTimeout(fail, opts.timeoutMs ?? DECODE_TIMEOUT_MS);
      signal?.addEventListener("abort", abort, { once: true });
      w.onmessage = (e) => {
        const m = e.data;
        if ("ready" in m) {
          if (sent) return;
          sent = true;
          const { width, height, data } = pixels;
          w.postMessage({ width, height, buffer: data.buffer }, [data.buffer]);
          return;
        }
        if (m.ok) done(() => resolve(m.scan));
        else fail();
      };
      // a worker that failed before it was ready never had the pixels: read them here
      w.onerror = () => (sent ? fail() : done(() => resolve(onThisThread(pixels))));
      w.onmessageerror = () => fail();
    });
  } finally {
    w.terminate();
  }
}
